import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
} from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { compose } from 'redux';

import {
  Form as FormikForm,
  withFormik,
  Field,
} from 'formik';

import {
  Dimmer,
  Form,
  Loader,
} from 'semantic-ui-react';

import { ConfirmDialog, captureException, TextAreaFormField } from '@playbookux/playbook-web-core';
import QuestionsForm from '../../../components/questions-form';
import SurveyPage from './survey-page';
import schema from './schema';

import {
  getProjectQuestions,
} from './graphql';

import { updateStudyMutation } from '../../../../core/services/graphql-requests';

import PreviewStudyButton from '../../../../core/components/preview-study-button';

import { config } from '../../../../config';

import {
  ButtonContainer,
  Container,
  FormContainer,
  Header,
  SubmitButton,
  CancelButton,
  Text,
  TextStatusError,
  HeaderSegmentContainer,
  SegmentTitle,
  Optional,
  ContextSegment,
  ContainerSurverPage,
  Loader as LoaderComponent,
  ContainerLoading,
} from './styles';

const ProjectEditQuestions = (props) => {
  const {
    values,
    errors,
    match,
    touched,
    setValues,
    setFieldValue,
    isValid,
  } = props;

  const location = useLocation();
  const history = useHistory();

  const { params: { projectId = '' } } = match;
  const surveyPageState = location.state || false;
  const isSurveyPageState = useMemo(() => !_.isEmpty(surveyPageState), [surveyPageState]);

  const [loading, setLoading] = useState(true);
  const [showDisregardConfirm, setShowDisregardConfirm] = useState(false);
  const [showSubmitConfirm, setShowSubmitConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(false);

  const loadProject = useCallback(async () => {
    try {
      const { project } = await getProjectQuestions(projectId);

      let tasks = [
        {
          label: '',
          addUrl: true,
          askTaskDifficulty: false,
        },
      ];

      const surveysPages = surveyPageState ? [
        {
          id: surveyPageState.id,
          pageName: surveyPageState.pageName,
          order: surveyPageState.order,
          tasks: [],
        },
      ] : [];

      if (project?.surveys.length > 0) {
        if (isSurveyPageState) {
          surveysPages[0].tasks = _.chain(surveyPageState.questions)
            .sortBy('questionOrder')
            .map((task) => ({
              ...task,
              range: task.questionOptions.length || [],
              randomize: task.randomize,
              ...(task.questionType === 'range' && {
                leftLabel: task.leftLabel,
                rightLabel: task.rightLabel,
              }),
            }))
            .value();
          setValues({ project, tasks: [], surveysPages });
        } else {
          tasks = _.find(project.surveys, (s) => s.surveyType === 'session');

          tasks = _.chain(tasks.questions)
            .sortBy('questionOrder')
            .map((task) => ({
              ...task,
              range: task.questionOptions.length,
              ...(task.questionType === 'range' && {
                leftLabel: task.leftLabel,
                rightLabel: task.rightLabel,
              }),
            }))
            .value();
          setValues({ project, tasks, surveysPages: [] });
        }
      }

      setLoading(false);
    } catch (ex) {
      setError(true);
      setLoading(false);
      captureException('PROJECT_EDIT_QUESTIONS_DID_MOUNT', ex);
    }
  }, [isSurveyPageState, projectId, setValues, surveyPageState]);

  const handleSubmitForm = async (preview = false) => {
    if (!isValid) {
      return;
    }
    setSaving(true);

    try {
      const { project, tasks, surveysPages } = values;
      const { surveys = [] } = project;
      const sessionSurvey = _.find(surveys, (v) => v.surveyType === 'session');

      if (isSurveyPageState) {
        let surveysPagesAttributes = [];
        let questionsAttributes = [];

        const surveysToUpdate = sessionSurvey.surveysPages;
        const surveyIndex = _.findIndex(surveysToUpdate, (survey) => survey.id === surveysPages[0].id);

        surveysPagesAttributes = surveysToUpdate.map((sp, index) => {
          let { questions } = sp;

          if (surveyIndex === index) {
            questions = surveysPages[0].tasks;
          }

          questionsAttributes = _.map(questions, (task) => ({
            id: task.id,
            label: task.label,
            randomize: task.randomize,
            questionType: task.questionType,
            displayTime: task.displayTime || 5,
            ...(task.leftLabel && { leftLabel: task.leftLabel }),
            ...(task.rightLabel && { rightLabel: task.rightLabel }),
            questionOptionsAttributes: task.questionOptions.map((v) => ({
              id: v.id,
              label: v.label,
              imageKey: v.imageKey,
            })),
          }));

          return ({
            id: sp.id,
            order: sp.order,
            pageName: sp.pageName,
            questionsAttributes,
          });
        });

        const surveysAttributes = {
          surveyType: 'session',
          id: sessionSurvey.id,
          testType: 'survey',
          surveysPagesAttributes,
        };

        const attributesProject = {
          studyContext: values.project.studyContext,
          surveysAttributes,
        };

        await updateStudyMutation(projectId, attributesProject);
      } else {
        let tasksAttributes = [];

        tasksAttributes = tasks.map((task) => ({
          id: task.id,
          label: task.label,
          randomize: task.randomize,
          questionType: task.questionType,
          ...(task.leftLabel && { leftLabel: task.leftLabel }),
          ...(task.rightLabel && { rightLabel: task.rightLabel }),
          questionOptionsAttributes: task.questionOptions.map((v) => ({
            id: v.id,
            label: v.label,
            imageKey: v.imageKey,
          })),
        }));

        const surveysAttributes = {
          surveyType: 'session',
          id: sessionSurvey.id,
          questionsAttributes: tasksAttributes,
        };

        const attributesProject = {
          studyContext: values.project.studyContext,
          surveysAttributes,
        };

        await updateStudyMutation(projectId, attributesProject);
      }

      setSaving(false);

      if (preview) {
        window.open(`${config.REACT_APP_PLATFORM_URI}/projects/${projectId}/preview-study`, '_blank');
      } else {
        history.push({ pathname: `/projects/${projectId}/study`, state: null });
      }
    } catch (ex) {
      captureException('EDIT_QUESTIONS_HANDLE_SUBMIT', ex);
      setSaving(false);
      setError(true);
    }
  };

  useEffect(() => {
    loadProject();
  }, [loadProject]);

  const handleCancel = () => {
    setShowDisregardConfirm(false);
    setShowSubmitConfirm(false);
  };

  const handleClose = () => {
    history.push({ pathname: `/projects/${projectId}/study`, state: null });
  };

  const openDialog = (dialog) => () => {
    if (dialog === 'disregard') {
      setShowDisregardConfirm(true);
    } else if (dialog === 'submit') {
      setShowSubmitConfirm(true);
    }
  };

  const handleRemoveQuestion = (index) => {
    const updatedTasks = _.pull(values.tasks, values.tasks[index]);
    setFieldValue(updatedTasks, 'tasks');
  };

  const handlePreviewStudy = () => {
    handleSubmitForm(true);
  };

  const renderQuestions = () => (
    <>
      <ContextSegment>
        <HeaderSegmentContainer noSpace>
          <SegmentTitle>Context</SegmentTitle>
          <Optional>- optional</Optional>
        </HeaderSegmentContainer>

        <Text>
          Create the context of your study so testers can put themselves
          in the correct mindset
        </Text>

        <Field
          name="project.studyContext"
          placeholder="Imagine you're looking for a new software for your role as a..."
          component={TextAreaFormField}
          height="84px"
        />
      </ContextSegment>

      <QuestionsForm
        hideTrashCan
        isEdit
        handleRemoveQuestion={handleRemoveQuestion}
        values={values}
        errors={errors}
        touched={touched}
        setFieldValue={setFieldValue}
        testType={values.project.testType}
      />

      <PreviewStudyButton
        onPreviewStudy={handlePreviewStudy}
      />
    </>
  );

  const renderSurveyPages = () => (
    <ContainerSurverPage>
      {values?.surveysPages.map((surveyPage, index) => (
        <SurveyPage
          key={surveyPage.id}
          values={surveyPage}
          index={index}
          hideTrashCan
          isEdit
          errors={errors.surveysPages && errors.surveysPages[index] ? errors.surveysPages[index] : []}
          touched={touched.surveysPages && touched.surveysPages[index] ? touched.surveysPages[index] : []}
          onRemoveQuestion={handleRemoveQuestion}
          setFieldValue={setFieldValue}
        />
      ))}
    </ContainerSurverPage>
  );

  return (
    <Container>
      {saving && (
        <Dimmer active={saving} inverted page>
          <Loader inverted data-loader="saving-edit-questions-loader" />
        </Dimmer>
      )}

      {error && (
        <FormContainer>
          <TextStatusError>
            Error while editing the project. Please try again.
          </TextStatusError>
        </FormContainer>
      )}

      <FormContainer>
        <Header>
          Edit Questions
        </Header>

        <Text>
          Changes will affect all unfilled sessions only.
        </Text>

        <FormikForm>
          <Form as="div">
            {loading && (
              <ContainerLoading>
                <LoaderComponent data-loader="edit-questions-loader" />
              </ContainerLoading>
            )}
            {(!loading && isSurveyPageState) && renderSurveyPages()}
            {(!loading && !isSurveyPageState) && renderQuestions()}
          </Form>
        </FormikForm>

        <ButtonContainer>
          <CancelButton
            onClick={openDialog('disregard')}
          >
            Don&apos;t Update
          </CancelButton>

          <SubmitButton
            next="true"
            onClick={openDialog('submit')}
            type="submit"
          >
            Update
          </SubmitButton>
        </ButtonContainer>
      </FormContainer>

      <ConfirmDialog
        show={showDisregardConfirm}
        warning="Are you sure you want to disregard updates? No changes will be made to the study questions and all changes will be lost."
        confirmText="Yes, Disregard Changes"
        confirmTextColor="black"
        confirmTextBackgroundColor="white"
        cancelText="No, Go Back"
        cancelTextColor="white"
        cancelTextBackgroundColor="#477AE9"
        handleConfirm={handleClose}
        handleClose={handleCancel}
        maxWidth="450px"
      />
      <ConfirmDialog
        show={showSubmitConfirm}
        warning="Are you sure you want to update the study questions?"
        confirmText="Yes, Update"
        confirmTextColor="white"
        confirmTextBackgroundColor="#477AE9"
        cancelText="Don't Update"
        cancelTextColor="black"
        cancelTextBackgroundColor="white"
        handleConfirm={() => handleSubmitForm()}
        handleClose={handleCancel}
        maxWidth="450px"
      />
    </Container>
  );
};

const matchType = PropTypes.shape({
  params: PropTypes.shape({
    projectId: PropTypes.string,
  }),
});

ProjectEditQuestions.propTypes = {
  values: PropTypes.shape().isRequired,
  setValues: PropTypes.func.isRequired,
  setFieldValue: PropTypes.func.isRequired,
  errors: PropTypes.shape().isRequired,
  touched: PropTypes.shape().isRequired,
  match: matchType.isRequired,
  isValid: PropTypes.bool.isRequired,
};

export default compose(
  withFormik({
    validationSchema: schema,
    enableReinitialize: true,
    mapPropsToValues: () => ({
      tasks: [],
      surveysPages: [],
    }),
    handleSubmit: () => undefined,
  }),
)(ProjectEditQuestions);
